import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { State } from '../../../dave-data-module/State';
import { getCommissionById } from '../../../dave-data-module/State/selectors/commission.selector';
import { getDepartments } from '../../../dave-data-module/State/selectors/department.selector';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { KostenstellenartEnum, KostenstellenartNames } from '../../../dave-data-module/entities/commission.entity';
import { getUsers, getUsersActive } from '../../../dave-data-module/State/selectors/users.selectors';
import {
    getUserToCommissionByCommissionId
} from '../../../dave-data-module/State/selectors/user-to-commission.selector';
import {
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogModule,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';
import { SelectSearchModule } from '../../../dave-utils-module/select-search/select-search.module';
import { UserEntity } from '../../../dave-data-module/entities/user.entity';
import {
    getEmployeesActiveSortedByNameStartWithMe
} from '../../../dave-data-module/State/selectors/employees.selectors';



export interface MissingCommissionValuesDialogData {
    /** All commissions that need Kostenstellenart / Kostenstelle / Bauleiter filled in. */
    CommissionIds: number[];
    BauleiterRoleId: number;
}

export interface MissingCommissionValues {
    Kostenstellenart: KostenstellenartEnum;
    DepartmentId: number;
    BauleiterUserId: number;
}
export type MissingCommissionValuesResult = Record<number, MissingCommissionValues>;

interface CommissionFormEntry {
    CommissionId: number;
    DisplayName: string;
    FormGroup: FormGroup<{
        Kostenstellenart: FormControl<KostenstellenartEnum | null>;
        DepartmentId: FormControl<number | null>;
        BauleiterUserId: FormControl<UserEntity | null>;
    }>;
}

@Component({
  selector: 'app-kapl-status4-dialog',
  standalone: true,
    imports: [CommonModule, MatLegacySelectModule, MatLegacyDialogModule, AppButtonModule, FontAwesomeModule, MatLegacyTooltipModule, ReactiveFormsModule, SelectSearchModule],
  templateUrl: './kapl-status4-dialog.component.html',
  styleUrls: ['./kapl-status4-dialog.component.scss']
})
export class KaplStatus4DialogComponent implements OnInit {
    public Entries: CommissionFormEntry[] = [];
    public CurrentIndex = 0;

    // Kostenstellenart is a plain enum, not something to fetch from the store -
    // same list as used for DetailListTemplateEntrys.CostCenter's specialInput.select.
    public KostenstellenartOptions: { Id: KostenstellenartEnum; Name: string }[] = Object.values(KostenstellenartEnum).map(
        (val) => ({
            Id: val,
            Name: KostenstellenartNames.get(val),
        }),
    );

    public DepartmentOptions$: Observable<{ Id: number; Name: string }[]>;

    public UserOptions$: Observable<UserEntity[]>;

    constructor(
        public DialogRef: MatDialogRef<KaplStatus4DialogComponent, MissingCommissionValuesResult>,
        @Inject(MAT_DIALOG_DATA) public Data: MissingCommissionValuesDialogData,
        private store: Store<State>,
        protected cls: CustomLabelService
        ,
    ) {}

    ngOnInit(): void {
        this.DepartmentOptions$ = this.store.select(getDepartments);
        this.UserOptions$ = combineLatest([
            this.store.select(getUsers),
            this.store.select(getEmployeesActiveSortedByNameStartWithMe),
        ]).pipe(
            map(([users, employees]) => {
                const activeUserIds = new Set(
                    employees
                        .filter((e) => !!e.UserId)
                        .map((e) => e.UserId),
                );

                return users.filter((user) => activeUserIds.has(user.Id));
            }),
        );
        const ids = this.Data?.CommissionIds ?? [];
        ids.forEach((id) => {
            combineLatest([this.store.select(getCommissionById({ id: id })), this.store.select(getUserToCommissionByCommissionId({ commissionId: id })), this.store.select(getUsers)])
                .pipe(take(1))
                .subscribe(([commission, u2c, users]) => {
                    const bauleiterU2c = u2c?.find((entry) => entry.RoleId === this.Data.BauleiterRoleId);
                    const bauleiterUser = users.find(
                        user => user.Id === bauleiterU2c?.UserId
                    );
                    const formGroup = new FormGroup({
                        Kostenstellenart: new FormControl<KostenstellenartEnum | null>(
                            {
                                value: commission?.Kostenstellenart ?? null,
                                disabled: commission?.Kostenstellenart != null,
                            },
                            {
                                validators: Validators.required,
                            }
                        ),
                        DepartmentId: new FormControl<number | null>(
                            {
                                value: commission?.DepartmentId ?? null,
                                disabled: commission?.DepartmentId != null,
                            },
                            {
                                validators: Validators.required,
                            }
                        ),
                        BauleiterUserId: new FormControl<UserEntity | null>(
                            {
                                value: bauleiterUser ?? null ?? null,
                                disabled: bauleiterUser != null,
                            },
                            {
                                validators: Validators.required,
                            }
                        ),
                    });
                    formGroup.updateValueAndValidity();

                    this.Entries.push({
                        CommissionId: id,
                        DisplayName: commission?.DisplayName,
                        FormGroup: formGroup,
                    });
                });
        });
    }

    public get Current(): CommissionFormEntry | undefined {
        return this.Entries[this.CurrentIndex];
    }

    public get IsMultiple(): boolean {
        return this.Entries.length > 1;
    }

    /** Save is only enabled once every commission's three required fields are set. */
    public get AllValid(): boolean {
        return this.Entries.length > 0 && this.Entries.every((e) => e.FormGroup.valid);
    }

    public get CurrentValid(): boolean {
        const value = this.Current?.FormGroup.getRawValue();

        return !!(
            value?.Kostenstellenart &&
            value?.DepartmentId &&
            value?.BauleiterUserId
        );
    }

    public Back(): void {
        if (this.CurrentIndex > 0) {
            this.CurrentIndex--;
        }
    }

    public Next(): void {
        if (this.CurrentIndex < this.Entries.length - 1) {
            this.CurrentIndex++;
        }
    }

    public Save(): void {
        if (!this.AllValid) {
            return;
        }
        const result: MissingCommissionValuesResult = {};

        this.Entries.forEach((e) => {
            const value = e.FormGroup.getRawValue();

            result[e.CommissionId] = {
                Kostenstellenart: value.Kostenstellenart,
                DepartmentId: value.DepartmentId,
                BauleiterUserId: (value.BauleiterUserId)?.Id,
            };
        });

        this.DialogRef.close(result);
    }

    public Cancel(): void {
        this.DialogRef.close();
    }
}
